import React, { useEffect, useState } from "react";
import "../../styles/clients.css";
import { Plus, Edit, Trash2, RefreshCw, X } from "lucide-react";
import { useGeneric } from "../../hooks/useGeneric";
import { confirmAndDelete } from "../../utils/deleteHelper";

const EMPTY_FORM = {
    area_id: null,
    area_name: "",
};

const Area = () => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [showForm, setShowForm] = useState(false);
    const [errors, setErrors] = useState({});
    const [search, setSearch] = useState("");
    const [saving, setSaving] = useState(false);

    const {
        data: areas = [],
        loading,
        fetchAll: fetchAreas,
        save: saveArea,
        remove: deleteArea,
    } = useGeneric({
        tableName: "areamaster",
        idColumn: "area_id",
        orderBy: "area_name",
        removeOnInsert: ["area_id"],
    });

    useEffect(() => {
        fetchAreas();
    }, [fetchAreas]);

    const set = (key, val) => {
        setForm((prev) => ({ ...prev, [key]: val }));
        setErrors((prev) => ({ ...prev, [key]: "" }));
    };

    // ── Validation ───────────────────────────────────────────────────
    const validate = () => {
        const errs = {};

        if (!(form.area_name ?? "").trim())
            errs.area_name = "Area name is required.";
        else if (form.area_name.length > 50)
            errs.area_name = "Max 50 characters.";

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ── Save ─────────────────────────────────────────────────────────
    const onSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            await saveArea(form);
            closeModal();
        } catch (err) {
            console.error("Save failed", err);
            alert("Save failed: " + (err.message || "Unknown error"));
        } finally {
            setSaving(false);
        }
    };

    // ── Modal helpers ────────────────────────────────────────────────
    const openAdd = () => {
        setForm(EMPTY_FORM);
        setErrors({});
        setShowForm(true);
    };

    const openEdit = (area) => {
        setForm({ ...area }); // ✅ fixed: spread the selected area, not the whole array
        setErrors({});
        setShowForm(true);
    };

    const closeModal = () => {
        setShowForm(false);
        setForm(EMPTY_FORM);
        setErrors({});
    };

    // ── Delete ───────────────────────────────────────────────────────
    const handleDelete = (row, e) => {
        e?.stopPropagation();
        confirmAndDelete({
            what: "area",
            name: row.area_name,
            run: () => deleteArea(row.area_id),
        });
    };

    // ── Filter ───────────────────────────────────────────────────────
    const filtered = (areas || [])
        .filter((u) =>
            u.area_name?.toLowerCase().includes(search.toLowerCase())
        )
        .sort((a, b) => a.area_name?.localeCompare(b.area_name));

    return (
        <div className="cl-page">
            {/* ── Panel ── */}
            <div className="cl-panel">
                <div className="cl-panel-heading">
                    <span>Area Details</span>
                    <button className="cl-add-btn" onClick={openAdd} title="Add Area">
                        <Plus size={16} />
                    </button>
                </div>

                {/* Search bar */}
                <div className="cl-search-bar">
                    <input
                        type="text"
                        placeholder="Search areas…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="cl-search-input"
                    />
                    <button className="cl-refresh-btn" onClick={fetchAreas} title="Refresh">
                        <RefreshCw size={15} />
                    </button>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="cl-loading">Loading…</div>
                ) : (
                    <div className="cl-table-wrapper">
                        <table className="cl-table">
                            <thead>
                                <tr>
                                    <th>Area ID</th>
                                    <th>Area Name</th>
                                    <th className="cl-actions-th">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={3} className="cl-empty">
                                            No areas found.
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map((u, i) => (
                                        <tr
                                            key={u.area_id ?? i}
                                            className={`${i % 2 === 0 ? "cl-row-even" : "cl-row-odd"} cl-row-clickable`}
                                            onClick={() => openEdit(u)}
                                            title="Click to edit"
                                        >
                                            <td data-label="Area ID">{u.area_id}</td>
                                            <td data-label="Area Name">{u.area_name}</td>
                                            <td className="cl-actions-td" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    className="cl-icon-btn cl-edit"
                                                    onClick={() => openEdit(u)}
                                                    title="Edit"
                                                >
                                                    <Edit size={15} /><span className="cl-icon-label">Edit</span>
                                                </button>
                                                <button
                                                    className="cl-icon-btn cl-delete"
                                                    onClick={(e) => handleDelete(u, e)}
                                                    title="Delete"
                                                >
                                                    <Trash2 size={15} /><span className="cl-icon-label">Delete</span>
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="cl-panel-footer" />
            </div>

            {/* ── Modal ── */}
            {showForm && (
                <div className="cl-modal-backdrop">
                    <div className="cl-modal-overlay" />
                    <div className="cl-modal" onClick={(e) => e.stopPropagation()}>

                        {/* Modal Header */}
                        <div className="cl-modal-header">
                            <h4 className="cl-modal-title">
                                {form.area_id ? "Edit Area" : "Add Area"}
                            </h4>
                            <button className="cl-modal-close" onClick={closeModal}>
                                <X size={18} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="cl-modal-body">
                            <form autoComplete="off">
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-10">
                                        <label className="cl-control-label">Area Name</label>
                                        <input
                                            type="text"
                                            className={`cl-form-control${errors.area_name ? " cl-input-error" : ""}`}
                                            placeholder="Area Name"
                                            maxLength={50}
                                            value={form.area_name}
                                            onChange={(e) => set("area_name", e.target.value)}
                                        />
                                        {errors.area_name && (
                                            <span className="cl-error-msg">{errors.area_name}</span>
                                        )}
                                    </div>
                                    <div className="cl-col-2">
                                        <label className="cl-control-label">Area ID</label>
                                        <input
                                            type="text"
                                            className="cl-form-control cl-readonly"
                                            placeholder="Auto"
                                            value={form.area_id ?? ""}
                                            readOnly
                                        />
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Modal Footer */}
                        <div className="cl-modal-footer">
                            <button className="cl-btn cl-btn-default" onClick={closeModal}>
                                Close
                            </button>
                            <button
                                className="cl-btn cl-btn-primary"
                                onClick={onSave}
                                disabled={saving}
                            >
                                {saving ? "Saving…" : form.area_id ? "Update" : "Add"}
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
};

export default Area;